import { defineStore } from 'pinia'
import type { ActivityJsonldDefaultRead, EmployeeJsonldDefaultRead } from '@/api'
import CBuffer from 'CBuffer'
import { useMercure } from '@/composables/useMercure.ts'
import { useGeneratedActivityStore, useGeneratedEmployeeAvatarStore, useGeneratedEmployeeStore } from '@/stores'
import { uniq } from 'lodash-es'

//region Live stream handler
useMercure().subscribe<ActivityJsonldDefaultRead>('Activity', (data: ActivityJsonldDefaultRead) => void useActivityFeedStore().push(data))
//endregion Live stream handler

type ActivityTypes = 'tags' | 'assignment' | 'effort-entry' | 'comment'

interface ActivityData_Tags {
  tags: (string | { name: string; href?: string; color?: string })[]
}

type ActivityData_Assignment = unknown
type ActivityData_EffortEntry = { effortId: number; effortEntryId: number; serviceType: string }[]

type TypedActivity<T extends ActivityTypes> = Omit<ActivityJsonldDefaultRead, 'data' | 'type'> & {
  type: ActivityTypes
  data: T extends 'tags' ? ActivityData_Tags : T extends 'assignment' ? ActivityData_Assignment : T extends 'effort-entry' ? ActivityData_EffortEntry : unknown
}

export interface ResolvedActivity<T extends ActivityTypes | unknown = unknown> {
  activity: TypedActivity<T>
  origin?: EmployeeJsonldDefaultRead
}

export const useActivityFeedStore = defineStore('activity-feed-store', {
  state: () => ({
    $data: new CBuffer<ResolvedActivity>(1)
  }),
  actions: {
    async prefetch(count = 8) {
      this.$data.size = count
      const activities = await useGeneratedActivityStore()
        .findAll(
          {
            query: {
              'order[createdAt]': 'desc',
              itemsPerPage: count
            }
          },
          true
        )
        .then((d) => d.value)
      const relevantEmps = uniq(activities.map<number>((act) => act.originId ?? 0).filter((n) => n))
      await useGeneratedEmployeeAvatarStore()
        .fetchAllByIri(
          relevantEmps.map((s) => s.toString()),
          true
        )
        .then((d) => d.value) //preload all at once
      for (const act of activities.toReversed()) {
        await this.push(act)
      }
    },
    async push(activity: ActivityJsonldDefaultRead) {
      const wrap: ResolvedActivity = { activity: activity as TypedActivity }

      const originDecision = activity.origin ?? activity.originId ?? undefined
      if (originDecision) {
        wrap.origin = await useGeneratedEmployeeStore()
          .fetchByIri(originDecision.toString())
          .then((d) => d.value)
      }
      //activity.id = (this.$data.last()?.id ?? 0) + 1
      this.$data.push(wrap)
    }
  }
})
